import {
	AllowedPaymentMethodsEnum,
	ApplicationStateEnum,
	ApplicationStateReasonEnum,
	InstallmentContractStateEnum,
	InstallmentStateEnum,
	InstallmentsLoanTypeEnum,
	OrderStateEnum,
} from '.spec/self-care/tsTypes';
import { take } from 'lodash';

import type { Amount } from '@skippay/core/@types/amount';
import type { Installment } from '@skippay/core/@types/payInThree';
import { sumAmounts, toFloat } from '@skippay/core/utils/amountUtils';
import type { Paginated } from '@skippay/core/utils/queryUtils';
import { type Maybe, isNotNil } from '@skippay/core/utils/types';

import { capNames, toIsoDate } from 'jarabot/utils';
import { nearestUnpaidInstallment } from 'utils/payInThreeUtils';

import { LIST_CAPS } from '../../constants';
import JarabotClosedApplicationsQuery from '../../queries/JarabotClosedApplicationsQuery.graphql';
import JarabotDeferredPaymentsQuery from '../../queries/JarabotDeferredPaymentsQuery.graphql';
import JarabotInstallmentApplicationsQuery from '../../queries/JarabotInstallmentApplicationsQuery.graphql';
import JarabotInstallmentsQuery from '../../queries/JarabotInstallmentsQuery.graphql';
import JarabotPayInThreeQuery from '../../queries/JarabotPayInThreeQuery.graphql';
import {
	type ApplicationInProgressItem,
	ApplicationInProgressType,
	type ClosedApplicationItem,
	type CustomerContextDomain,
	type DeferredPaymentItem,
	type InstallmentItem,
	type PayInThreeItem,
	type PayInThreeScheduleItem,
	type Sanctions,
} from '../../types';
import {
	DEFERRED_PAYMENT_ACTIVE_FILTER,
	DEFERRED_PAYMENT_CLOSED_FILTER,
	INSTALLMENTS_ACTIVE_FILTER,
	PAY_IN_THREE_ACTIVE_FILTER,
	PAY_IN_THREE_CLOSED_FILTER,
} from '../filters';
import type { DomainSource } from '../types';

const NEWEST_FIRST_SORT = '-createdDate';

interface SanctionClaims {
	sanctionClaim?: Maybe<Amount>;
	sanctionInterestClaim?: Maybe<Amount>;
	sanctionInterestLegalClaim?: Maybe<Amount>;
}

interface OrderRow {
	createdDate?: Maybe<string>;
	state?: Maybe<OrderStateEnum>;
	totalPrice?: Maybe<Amount>;
	items?: Maybe<Array<{ name?: Maybe<string>; state?: Maybe<OrderStateEnum> }>>;
}

interface ApplicationRow {
	id?: Maybe<string>;
	isPayable?: Maybe<boolean>;
	eshopAdditionalInfo?: Maybe<{ name?: Maybe<string> }>;
	state?: Maybe<ApplicationStateEnum>;
	stateReason?: Maybe<ApplicationStateReasonEnum>;
	order?: Maybe<OrderRow>;
	additionalInfoInstallments?: Maybe<{ canApplyForInstallments?: Maybe<boolean> }>;
	financing?: Maybe<{
		currentDebt?: Maybe<Amount>;
		payInThree?: Maybe<{ installments?: Maybe<Installment[]> }>;
		deferredPayment?: Maybe<
			SanctionClaims & { currentDpd?: Maybe<number>; dueDate?: Maybe<string> }
		>;
	}>;
}

export const hasReturnedItems = (order?: Maybe<OrderRow>): true | undefined =>
	order?.items?.some((item) => item.state === OrderStateEnum.RETURNED) || undefined;

const sumClaims = (claims: ReadonlyArray<Maybe<Amount>>): Amount =>
	sumAmounts(claims.filter(isNotNil));

interface SanctionsInfo {
	sanctions: Sanctions;
	sanctionsTotal: number;
}

export const toSanctionsInfo = (rows: readonly SanctionClaims[]): SanctionsInfo | undefined => {
	const reminders = sumClaims(rows.map((row) => row.sanctionClaim));
	const contractualPenalty = sumClaims(rows.map((row) => row.sanctionInterestClaim));
	const legalInterest = sumClaims(rows.map((row) => row.sanctionInterestLegalClaim));
	const total = sumAmounts([reminders, contractualPenalty, legalInterest]);
	if (toFloat(total) <= 0) return undefined;
	return {
		sanctions: {
			reminders: toFloat(reminders),
			contractualPenalty: toFloat(contractualPenalty),
			legalInterest: toFloat(legalInterest),
		},
		sanctionsTotal: toFloat(total),
	};
};

const toOrderItemNames = (order?: Maybe<OrderRow>): string[] | undefined =>
	order?.items
		? capNames(
				order.items.map((item) => item.name),
				LIST_CAPS.orderItemNames
			)
		: undefined;

const toSchedule = (installments?: Maybe<Installment[]>): PayInThreeScheduleItem[] | undefined =>
	installments && installments.length
		? installments.map((installment) => ({
				number: installment.number ?? undefined,
				dueDate: toIsoDate(installment.dueDate),
				amount: installment.balance ? toFloat(installment.balance) : undefined,
				paymentDate: toIsoDate(installment.paymentDate),
				daysOverdue: installment.currentDpd ?? undefined,
			}))
		: undefined;

export const payInThreeSource: DomainSource<{
	myContractApplications?: Paginated<ApplicationRow>;
}> = {
	domains: ['payInThree'],
	query: JarabotPayInThreeQuery,
	variables: {
		limit: LIST_CAPS.payInThree,
		sort: NEWEST_FIRST_SORT,
		filter: PAY_IN_THREE_ACTIVE_FILTER,
	},
	toModel: (data) => {
		// the Apollo cache unions paginated rows across reads, so the query `limit` alone does
		// not bound what lands here
		const rows = take(data.myContractApplications?.data, LIST_CAPS.payInThree);
		const payInThree: PayInThreeItem[] = rows.map((app) => {
			const installments = app.financing?.payInThree?.installments ?? [];
			const next = nearestUnpaidInstallment(installments);
			const sanctionsInfo = toSanctionsInfo(installments);
			const debt = app.financing?.currentDebt ? toFloat(app.financing.currentDebt) : undefined;
			return {
				eshop: app.eshopAdditionalInfo?.name ?? undefined,
				state: app.state ?? undefined,
				stateReason: app.stateReason ?? undefined,
				total: app.order?.totalPrice ? toFloat(app.order.totalPrice) : undefined,
				debt,
				next: next
					? {
							date: toIsoDate(next.dueDate),
							amount: next.balance ? toFloat(next.balance) : undefined,
						}
					: undefined,
				daysOverdue: next?.currentDpd ?? undefined,
				canPay: app.isPayable ?? undefined,
				items: toOrderItemNames(app.order),
				schedule: toSchedule(installments),
				sanctions: sanctionsInfo?.sanctions,
				sanctionsTotal: sanctionsInfo?.sanctionsTotal,
				orderState: app.order?.state ?? undefined,
				hasReturnedItems: hasReturnedItems(app.order),
				hasOverpayment: debt === undefined ? undefined : debt < 0,
				canSplitToInstallments:
					app.additionalInfoInstallments?.canApplyForInstallments ?? undefined,
				orderCreatedDate: toIsoDate(app.order?.createdDate),
			};
		});
		return { payInThree };
	},
};

export const deferredPaymentsSource: DomainSource<{
	myContractApplications?: Paginated<ApplicationRow>;
}> = {
	domains: ['deferredPayments'],
	query: JarabotDeferredPaymentsQuery,
	variables: {
		limit: LIST_CAPS.deferredPayments,
		sort: NEWEST_FIRST_SORT,
		filter: DEFERRED_PAYMENT_ACTIVE_FILTER,
	},
	toModel: (data) => {
		const rows = take(data.myContractApplications?.data, LIST_CAPS.deferredPayments);
		const deferredPayments: DeferredPaymentItem[] = rows.map((app) => {
			const currentDebt = app.financing?.currentDebt;
			const debt = currentDebt ? toFloat(currentDebt) : undefined;
			const deferredPayment = app.financing?.deferredPayment;

			return {
				payable: app.id && currentDebt ? { id: app.id, value: currentDebt } : undefined,
				eshop: app.eshopAdditionalInfo?.name ?? undefined,
				state: app.state ?? undefined,
				stateReason: app.stateReason ?? undefined,
				total: app.order?.totalPrice ? toFloat(app.order.totalPrice) : undefined,
				debt,
				dueDate: toIsoDate(deferredPayment?.dueDate),
				amountToPay: debt,
				daysOverdue: deferredPayment?.currentDpd ?? undefined,
				canPay: app.isPayable ?? undefined,
				items: toOrderItemNames(app.order),
				sanctions: deferredPayment ? toSanctionsInfo([deferredPayment])?.sanctions : undefined,
				orderState: app.order?.state ?? undefined,
				hasReturnedItems: hasReturnedItems(app.order),
				orderCreatedDate: toIsoDate(app.order?.createdDate),
			};
		});
		return { deferredPayments };
	},
};

interface ClosedApplicationRow {
	state?: Maybe<ApplicationStateEnum>;
	eshopAdditionalInfo?: Maybe<{ name?: Maybe<string> }>;
	order?: Maybe<OrderRow>;
	financing?: Maybe<{ paymentDate?: Maybe<string> }>;
}

const toClosedItem = (app: ClosedApplicationRow): ClosedApplicationItem => ({
	eshop: app.eshopAdditionalInfo?.name ?? undefined,
	state: app.state ?? undefined,
	total: app.order?.totalPrice ? toFloat(app.order.totalPrice) : undefined,
	paidDate: toIsoDate(app.financing?.paymentDate),
	orderState: app.order?.state ?? undefined,
	hasReturnedItems: hasReturnedItems(app.order),
});

type ClosedApplicationsDomain = Extract<
	CustomerContextDomain,
	'closedPayInThree' | 'closedDeferredPayments'
>;

const closedApplicationsSource = (
	domain: ClosedApplicationsDomain,
	filter: string,
	limit: number
): DomainSource<{ myContractApplications?: Paginated<ClosedApplicationRow> }> => ({
	domains: [domain],
	query: JarabotClosedApplicationsQuery,
	variables: { limit, sort: NEWEST_FIRST_SORT, filter },
	toModel: (data) => {
		const items = take(data.myContractApplications?.data, limit).map(toClosedItem);
		return domain === 'closedPayInThree'
			? { closedPayInThree: items }
			: { closedDeferredPayments: items };
	},
});

export const closedPayInThreeSource = closedApplicationsSource(
	'closedPayInThree',
	PAY_IN_THREE_CLOSED_FILTER,
	LIST_CAPS.closedPayInThree
);

export const closedDeferredPaymentsSource = closedApplicationsSource(
	'closedDeferredPayments',
	DEFERRED_PAYMENT_CLOSED_FILTER,
	LIST_CAPS.closedDeferredPayments
);

interface InstallmentContractRow {
	accelerationDate?: Maybe<string>;
	allowedPaymentMethods?: Maybe<AllowedPaymentMethodsEnum[]>;
	balance?: Maybe<Amount>;
	installments?: Maybe<Array<SanctionClaims & { state?: Maybe<InstallmentStateEnum> }>>;
	installmentsCount?: Maybe<number>;
	interestRate?: Maybe<string>;
	loanType?: Maybe<InstallmentsLoanTypeEnum>;
	maximumPayment?: Maybe<Amount>;
	nextInstallment?: Maybe<{
		dueDate?: Maybe<string>;
		balance?: Maybe<Amount>;
		daysPastDueDate?: Maybe<number>;
	}>;
	repaymentDate?: Maybe<string>;
	rpsn?: Maybe<string>;
	state?: Maybe<InstallmentContractStateEnum>;
	title?: Maybe<string>;
	variableSymbol?: Maybe<string>;
}

export const installmentsSource: DomainSource<{
	myInstallmentsContracts?: Paginated<InstallmentContractRow>;
}> = {
	domains: ['installments'],
	query: JarabotInstallmentsQuery,
	variables: { limit: LIST_CAPS.installments, filter: INSTALLMENTS_ACTIVE_FILTER },
	toModel: (data) => {
		const rows = take(data.myInstallmentsContracts?.data, LIST_CAPS.installments);
		const installments: InstallmentItem[] = rows.map((contract) => {
			const next = contract.nextInstallment;
			const sanctionsInfo = toSanctionsInfo(contract.installments ?? []);
			return {
				type: contract.loanType ?? undefined,
				state: contract.state ?? undefined,
				next: next
					? {
							date: toIsoDate(next.dueDate),
							amount: next.balance ? toFloat(next.balance) : undefined,
						}
					: undefined,
				daysOverdue: next?.daysPastDueDate ?? undefined,
				earlyRepayment: contract.maximumPayment ? toFloat(contract.maximumPayment) : undefined,
				remainingDebt: contract.balance ? toFloat(contract.balance) : undefined,
				installmentsCount: contract.installmentsCount ?? undefined,
				paidCount: contract.installments
					? contract.installments.filter(
							(installment) =>
								installment.state === InstallmentStateEnum.PAID ||
								installment.state === InstallmentStateEnum.ZERO_CLAIM
						).length
					: undefined,
				interestRate: contract.interestRate ?? undefined,
				rpsn: contract.rpsn ?? undefined,
				title: contract.title ?? undefined,
				repaymentDate: toIsoDate(contract.repaymentDate),
				accelerationDate: toIsoDate(contract.accelerationDate),
				allowedPaymentMethods: contract.allowedPaymentMethods ?? undefined,
				sanctionsTotal: sanctionsInfo?.sanctionsTotal,
				sanctions: sanctionsInfo?.sanctions,
				variableSymbol: contract.variableSymbol ?? undefined,
			};
		});
		return { installments };
	},
};

interface InstallmentApplicationRow {
	id?: Maybe<string>;
	state?: Maybe<InstallmentContractStateEnum>;
}

interface InstallmentApplicationsData {
	inProcessing?: Paginated<InstallmentApplicationRow>;
	waitingForApproval?: Paginated<InstallmentApplicationRow>;
}

const toApplicationsInProgress = (
	data: InstallmentApplicationsData
): ApplicationInProgressItem[] => {
	const stateById = new Map<string, InstallmentContractStateEnum | undefined>();
	const withoutId: Array<InstallmentContractStateEnum | undefined> = [];
	[data.inProcessing, data.waitingForApproval].forEach((slice) => {
		(slice?.data ?? []).forEach((row) => {
			if (row.id) stateById.set(row.id, row.state ?? undefined);
			else withoutId.push(row.state ?? undefined);
		});
	});
	return take([...stateById.values(), ...withoutId], LIST_CAPS.applicationsInProgress).map(
		(state) => ({ type: ApplicationInProgressType.INSTALLMENTS, state })
	);
};

export const installmentApplicationsSource: DomainSource<InstallmentApplicationsData> = {
	domains: ['applicationsInProgress'],
	query: JarabotInstallmentApplicationsQuery,
	variables: { limit: LIST_CAPS.applicationsInProgress },
	toModel: (data) => ({ applicationsInProgress: toApplicationsInProgress(data) }),
};
